import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IoAdd } from "react-icons/io5";
import { MdAdd, MdWarning } from "react-icons/md";

import { ICustomPoint, IFamilyMember } from "../models/IFamily";
import { ELCID, IMediaToolkitNavPage, IPromisePoint } from "../models/IMediaToolkit";
import { PromisePointPicker } from "../components/PromisePointPicker";
import AAPService from "../services/AAPService";
import { ITranslations } from "../data/strings";

export interface ITopicPageProps extends IMediaToolkitNavPage {
    scienceTitle: string;
    talktoTitle: string;
    translations: ITranslations;
    updateAllMembers: (members: IFamilyMember[]) => void;
    addCustomPoint: (promiseId: number, topicId: number) => void;
    updateCustomPoint: (point: ICustomPoint) => void;
    deleteCustomPoint: (idx: number) => void;
    togglePriority: (pageId: number) => void;
}

export const TopicPage: React.FunctionComponent<ITopicPageProps> = (props) => {
    // bail if no family data is filled out
    const navigate = useNavigate();

    const { topicid } = useParams();
    const topic = props.topics.filter((topic) => topic.id.toString() === topicid)[0];

    React.useEffect(() => {
        if (props.family.name === "" || props.family.members[0].name === "") {
            navigate("/family");
        }
    });

    return (
        <>
            <div className="card-header bg-aap-creamy-gray-500 text-aap-dk-red-500 px-5 xl:px-10 py-5 pt-10">
                <h1 className="text-4xl mb-5">{decodeURIComponent(topic.title)}</h1>
                <p className="text-sm" dangerouslySetInnerHTML={{ __html: decodeURIComponent(topic.description) }}></p>
            </div>
            <div className="card-body bg-aap-cream-500 p-5 pt-0 xl:p-10 xl:pt-0">
                {props.family.priorities.indexOf(topic.id) === -1 ? (
                    <div className="-mx-5 xl:-mx-10 p-1 bg-aap-orange-500 text-white flex justify-between">
                        <div className="text-sm py-1 px-3 flex gap-2">
                            <MdWarning className="inline" size={20} />
                            <span>{props.translations.pageNotPriorityBlurb}</span>
                        </div>
                        <button
                            type="button"
                            className="text-xs border border-white text-white hover:bg-white hover:text-aap-orange-500 px-2 py-1 rounded-md"
                            onClick={() => {
                                props.togglePriority(topic.id);
                            }}
                            id={`btn_priorities_deferredAdd_${ELCID[props.lcid]}`}
                        >
                            <MdAdd className="inline" /> {props.translations.addPageAsPriority}
                        </button>
                    </div>
                ) : null}
                {props.ctaMap}
                {topic.promises.map((promise, promiseIdx) => {
                    const promiseTitle = decodeURIComponent(promise.title);

                    return (
                        <div className="mb-10" key={`promise-item-${promiseTitle}`}>
                            <h2 className="text-xl lg:text-2xl mb-2 flex gap-2 lg:gap-3">
                                {React.createElement(AAPService.GetMaterialIcon(promise.icon || 'help_outline'), { size: 44, className: "inline-block ml-3 mr-2 mb-1 shrink-0" })}
                                <div className="flex flex-col justify-center">{promiseTitle}</div>
                            </h2>
                            {(promise.allPoints || []).map((point, pointIdx) => {
                                return (
                                    <PromisePointPicker
                                        point={point}
                                        familymembers={props.family.members}
                                        key={`promisePointPicker-${promiseTitle}-point-${pointIdx}`}
                                        updateAllMembers={props.updateAllMembers}
                                        id={point.id}
                                        defaultIcon={topic.icon}
                                        readonly={props.readonly}
                                        talktoTitle={props.talktoTitle}
                                        scienceTitle={props.scienceTitle}
                                        lcid={props.lcid}
                                        translations={props.translations}
                                    />
                                );
                            })}
                            {props.family.customPoints
                                .filter((point) => point.promiseId === promise.id && point.topicId === topic.id)
                                .map((point, pointIdx) => {
                                    return (
                                        <PromisePointPicker
                                            point={point}
                                            id={point.id}
                                            familymembers={props.family.members}
                                            key={`promisePointPicker-${promiseTitle}-point-${pointIdx}`}
                                            updateAllMembers={props.updateAllMembers}
                                            updatePoint={(updatedPoint: IPromisePoint | ICustomPoint) => {
                                                props.updateCustomPoint(updatedPoint as ICustomPoint);
                                            }}
                                            deletePoint={() => {
                                                props.deleteCustomPoint(point.id);
                                            }}
                                            defaultIcon={topic.icon}
                                            readonly={props.readonly}
                                            talktoTitle={props.talktoTitle}
                                            scienceTitle={props.scienceTitle}
                                            lcid={props.lcid}
                                            translations={props.translations}
                                            editable
                                        />
                                    );
                                })}
                            {!props.readonly ? (
                                <button
                                    type="button"
                                    onClick={() => props.addCustomPoint(promise.id, topic.id)}
                                    className="border border-aap-red-500 text-aap-red-500 bg-white hover:bg-aap-red-500 hover:text-white text-sm py-1 px-2 rounded-sm"
                                >
                                    <IoAdd size={16} className="inline" /> {props.translations.addAnother}
                                </button>
                            ) : null}
                            {promiseIdx !== topic.promises.length - 1 ? (
                                <hr className="mt-10 border-aap-gray-200" />
                            ) : null}
                        </div>
                    );
                })}
                {props.footer ? (
                    <div className="pt-10">
                        <div dangerouslySetInnerHTML={{ __html: props.footer || "" }}></div>
                    </div>
                ) : null}
                <div className="mobileNav py-2 flex justify-between lg:hidden">
                    {props.backButton}
                    {props.continueButton}
                </div>
            </div>
        </>
    );
};
